/**
 * Shared browser-load recovery contract.
 *
 * This module intentionally has no React or service-worker dependencies.  The
 * error boundary and the Vite preload listener both use it so a preload error
 * cannot start a second recovery while the boundary is already recovering.
 */

export const APP_CACHE_PREFIX = "universo-oro-v";
export const CURRENT_APP_CACHE = "universo-oro-v8";
export const PREVIOUS_APP_CACHE_LIMIT = 1;
export const RECOVERY_ATTEMPT_KEY = "__uo_browser_recovery_attempted";
export const RECOVERY_QUERY_KEY = "_uo_recovery";
export const RECOVERY_CLEANUP_TIMEOUT_MS = 750;
export const BROWSER_RECOVERY_EVENT = "uo:browser-recovery";

const VERSIONED_APP_CACHE_RE = /^universo-oro-v\d+$/;
const CHUNK_ERROR_RE =
  /Loading (?:chunk|CSS chunk)|dynamically imported module|Importing a module script failed|ChunkLoadError|Failed to fetch dynamically imported module|preload/i;
const OFFLINE_ERROR_RE =
  /\boffline\b|ERR_(?:INTERNET_DISCONNECTED|NETWORK_CHANGED)/i;

export type BrowserRecoveryKind = "chunk" | "css-preload" | "offline" | "other";

export type BrowserRecoveryClassification = {
  kind: BrowserRecoveryKind;
  recoverable: boolean;
  offline: boolean;
  message: string;
};

/**
 * The event payload is deliberately reduced to a stable category.  Error
 * messages can contain a full asset URL (and, in some applications, query
 * parameters), so callers should not log the original value.
 */
export function classifyBrowserRecoveryError(
  error: unknown,
  hint?: "css-preload" | "offline",
): BrowserRecoveryClassification {
  const message = errorMessage(error);
  const chunkLike = CHUNK_ERROR_RE.test(message);
  const offline =
    hint === "offline" ||
    (typeof navigator !== "undefined" && navigator.onLine === false) ||
    (!chunkLike && hint !== "css-preload" && OFFLINE_ERROR_RE.test(message));

  if (hint === "css-preload") {
    return {
      kind: offline ? "offline" : "css-preload",
      recoverable: !offline,
      offline,
      message: offline ? "offline" : "css-preload",
    };
  }
  if (chunkLike) {
    return {
      kind: offline ? "offline" : "chunk",
      recoverable: !offline,
      offline,
      message: offline ? "offline" : "chunk",
    };
  }
  if (offline) {
    return { kind: "offline", recoverable: false, offline: true, message: "offline" };
  }
  return { kind: "other", recoverable: false, offline: false, message: "other" };
}

export function isChunkLoadError(error: unknown): boolean {
  const classification = classifyBrowserRecoveryError(error);
  return classification.kind === "chunk" || classification.kind === "css-preload";
}

function errorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return `${error.name} ${error.message}`;
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" ? message : "";
  }
  return "";
}

/**
 * Avoid putting URLs, query strings, email addresses, or arbitrary exception
 * text into the client-error payload.  Error categories are the useful signal
 * for this recovery path.
 */
export function sanitizeRecoveryText(value: unknown, fallback = "browser recovery error"): string {
  const text = typeof value === "string" ? value : fallback;
  return text
    .replace(/https?:\/\/[^\s)]+/gi, "[url]")
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[email]")
    .replace(/[?&#][^\s)]+/g, "[query]")
    .slice(0, 300);
}

let recoveryInFlight = false;
let memoryAttempted = false;
let recoverySuppressed = false;

type EarlyRecoveryState = {
  kind?: BrowserRecoveryKind;
  offline?: boolean;
};

declare global {
  interface Window {
    __uoEarlyAssetFailure?: EarlyRecoveryState;
    __uoEarlyAssetRecoveryClaimed?: boolean;
  }
}

function readAttemptMarker(): boolean {
  if (memoryAttempted) return true;
  try {
    if (typeof window !== "undefined" && window.__uoEarlyAssetRecoveryClaimed === true) {
      return true;
    }
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(RECOVERY_ATTEMPT_KEY) === "1") {
      return true;
    }
  } catch {
    // Private browsing / denied storage is a supported browser mode.
  }
  try {
    return (
      typeof window !== "undefined" &&
      new URL(window.location.href).searchParams.get(RECOVERY_QUERY_KEY) === "1"
    );
  } catch {
    return false;
  }
}

export function hasBrowserRecoveryAttempted(): boolean {
  return readAttemptMarker();
}

export function markBrowserRecoveryAttempted(): void {
  memoryAttempted = true;
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(RECOVERY_ATTEMPT_KEY, "1");
    }
  } catch {
    // The URL marker below keeps the guard across a document reload.
  }
}

export function clearBrowserRecoveryAttempt(): void {
  memoryAttempted = false;
  recoveryInFlight = false;
  recoverySuppressed = false;
  try {
    if (typeof sessionStorage !== "undefined") sessionStorage.removeItem(RECOVERY_ATTEMPT_KEY);
  } catch {
    // Ignore denied storage.
  }
  if (typeof window !== "undefined") {
    delete window.__uoEarlyAssetRecoveryClaimed;
    delete window.__uoEarlyAssetFailure;
  }
}

export function markBrowserRecoverySuppressed(): void {
  recoverySuppressed = true;
}

export function hasBrowserRecoverySuppressed(): boolean {
  return recoverySuppressed;
}

/**
 * A recovery URL marker is cleared only after a mounted application has
 * remained free of the boundary fallback and the document reached `complete`.
 * This prevents an early boot reset from turning a failed reload into a loop.
 */
export function scheduleStableBrowserRecoveryReset(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!hasBrowserRecoveryAttempted()) return;
  const startedAt = Date.now();
  const check = () => {
    if (!hasBrowserRecoveryAttempted() || recoverySuppressed) return;
    const boundaryVisible = Boolean(document.querySelector("[data-testid='app-error-boundary']"));
    const earlyFailure = window.__uoEarlyAssetFailure;
    const earlyFallbackVisible = Boolean(document.getElementById("uo-asset-fallback"));
    if (document.readyState === "complete" && !boundaryVisible && !earlyFailure && !earlyFallbackVisible) {
      clearBrowserRecoveryAttempt();
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete(RECOVERY_QUERY_KEY);
        url.searchParams.delete("_cb");
        window.history.replaceState(window.history.state, document.title, url.toString());
      } catch {
        // The marker in sessionStorage is still cleared above.
      }
      return;
    }
    if (Date.now() - startedAt < 60_000) window.setTimeout(check, 5_000);
  };
  window.setTimeout(check, 15_000);
}

/**
 * Atomically claims the one automatic recovery allowed for this tab fault.
 * The URL marker is written by forceFreshReload, because it is the only
 * cross-reload fallback when sessionStorage is unavailable.
 */
export function claimBrowserRecovery(): boolean {
  if (recoveryInFlight || readAttemptMarker()) return false;
  recoveryInFlight = true;
  markBrowserRecoveryAttempted();
  if (typeof window !== "undefined") window.__uoEarlyAssetRecoveryClaimed = true;
  return true;
}

export function markBrowserRecoveryHandled(error: unknown): void {
  if (error && (typeof error === "object" || typeof error === "function")) {
    handledErrors.add(error as object);
  }
}

export function wasBrowserRecoveryHandled(error: unknown): boolean {
  return !!error && (typeof error === "object" || typeof error === "function")
    ? handledErrors.has(error as object)
    : false;
}

const handledErrors = new WeakSet<object>();

export function notifyBrowserRecoverySuppressed(offline: boolean): void {
  if (typeof window === "undefined") return;
  markBrowserRecoverySuppressed();
  window.__uoEarlyAssetFailure = {
    kind: "css-preload",
    offline,
  };
  window.dispatchEvent(
    new CustomEvent(BROWSER_RECOVERY_EVENT, {
      detail: {
        offline,
        kind: offline ? "offline" : "css-preload",
        reloadSuppressed: true,
        telemetry: true,
      },
    }),
  );
}

export function isAppCacheName(name: string): boolean {
  return VERSIONED_APP_CACHE_RE.test(name);
}

type RecoveryServiceWorkerRegistration = {
  scriptURL?: string;
  scope?: string;
  unregister(): Promise<boolean>;
};

type RecoveryServiceWorker = {
  getRegistrations(): Promise<readonly RecoveryServiceWorkerRegistration[]>;
};

type RecoveryCacheStorage = {
  keys(): Promise<string[]>;
  delete(name: string): Promise<boolean>;
};

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T | undefined> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(undefined), timeoutMs);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(undefined);
      },
    );
  });
}

function isAppServiceWorker(registration: RecoveryServiceWorkerRegistration): boolean {
  if (typeof location === "undefined") return registration.scriptURL === "/sw.js";
  try {
    const origin = location.origin;
    return (
      registration.scriptURL === `${origin}/sw.js` ||
      registration.scriptURL === `${origin}/sw.js?` ||
      registration.scriptURL?.startsWith(`${origin}/sw.js?`) === true
    );
  } catch {
    return false;
  }
}

/**
 * Remove only the active app cache and this app's own service worker.  The
 * previous version remains available for old tabs and unrelated caches/SWs
 * belonging to other applications are never touched.
 *
 * Every operation is best-effort and bounded: a hung Cache Storage or SW
 * implementation must never prevent the cache-busted navigation.
 */
export async function clearChunkRecoveryCaches(
  serviceWorker: RecoveryServiceWorker | undefined =
    typeof navigator !== "undefined" ? navigator.serviceWorker : undefined,
  cacheStorage: RecoveryCacheStorage | undefined =
    typeof caches !== "undefined" ? caches : undefined,
): Promise<void> {
  const operations: Promise<unknown>[] = [];
  if (serviceWorker?.getRegistrations) {
    operations.push(
      withTimeout(serviceWorker.getRegistrations(), RECOVERY_CLEANUP_TIMEOUT_MS).then((registrations) => {
        if (!registrations) return;
        return Promise.all(
          registrations
            .filter(isAppServiceWorker)
            .map((registration) =>
              withTimeout(registration.unregister(), RECOVERY_CLEANUP_TIMEOUT_MS),
            ),
        );
      }),
    );
  }
  if (cacheStorage?.keys) {
    operations.push(
      withTimeout(cacheStorage.keys(), RECOVERY_CLEANUP_TIMEOUT_MS).then((names) => {
        if (!names) return;
        // Keep prior hashed assets readable by old tabs.  Only the active
        // version is considered poisoned after a chunk failure.
        if (!names.includes(CURRENT_APP_CACHE)) return;
        return withTimeout(cacheStorage.delete(CURRENT_APP_CACHE), RECOVERY_CLEANUP_TIMEOUT_MS);
      }),
    );
  }
  await withTimeout(Promise.allSettled(operations), RECOVERY_CLEANUP_TIMEOUT_MS);
}
